import {
  createContext,
  ReactNode,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Association, CATEGORYS } from "../@data/association";
import { collection, getDocs, query } from "firebase/firestore";
import { firestore } from "../services/firebase";

export interface AssociationsContextModel {
  categories: typeof CATEGORYS;
  associations: Association[];
  loadingAssociations: boolean;
}

export const AssociationsContext = createContext<AssociationsContextModel>(
  {} as AssociationsContextModel
);

export const AssociationsProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [associations, setAssociations] = useState<Association[]>(
    [] as Association[]
  );
  const [loadingAssociations, setLoadingAssociations] = useState<boolean>(true);

  const handleGetAssociations = useCallback(async () => {
    const q = query(collection(firestore, "associations"));
    getDocs(q)
      .then((snapshot) => {
        let associationsData = [] as Association[];
        snapshot.forEach((association) =>
          associationsData.push({
            ...(association.data() as Association),
            associationId: association.id,
          })
        );
        setAssociations(associationsData);
      })
      .finally(() => setLoadingAssociations(false));
  }, []);

  useEffect(() => {
    handleGetAssociations();
  }, [handleGetAssociations]);

  const categories = useMemo(() => {
    let associationsCategories = associations.map((a) => a.category);
    let justCategoriesInAssociations = CATEGORYS.filter((c) =>
      associationsCategories.includes(c.key)
    );
    return justCategoriesInAssociations;
  }, [associations]);

  return (
    <AssociationsContext.Provider
      value={{
        categories,
        associations,
        loadingAssociations,
      }}
    >
      {children}
    </AssociationsContext.Provider>
  );
};

export const useAssociations = () => useContext(AssociationsContext);
